import { Component, DestroyRef, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import BigNumber from 'bignumber.js';
import { Currencies } from '@app/shared/store/currencies/currencies-facade.service';
import { AppCurrencyFloorPipe } from '@app/shared/pipes/app-currency-floor.pipe';
import { Locales, UserInfoProfileService } from '@app/shared/services/user-info-profile.service';
import { TooltipPosition } from '@app/shared/components/tooltip/tooltip.enum';
import { TranslatePipe } from '@ngx-translate/core';
import { CurrencyDto } from '@app/generated/models/currency-dto';

@Component({
  selector: 'app-currency-value',
  imports: [CommonModule, TranslatePipe],
  templateUrl: './currency-value.component.html',
  host: { class: 'font-[inherit]' },
})
export class CurrencyValueComponent implements OnInit, OnChanges {
  @Input() value: number | null | undefined = 0;
  @Input() currency: CurrencyDto['name'] = '';
  @Input() hideCurrency: boolean = false;
  @Input() translateCurrency = false;
  @Input() tooltipPosition: TooltipPosition = TooltipPosition.left;
  @Input() tooltipUsePopover = true;
  @Input() tooltipClass?: string;

  public value$: Observable<string | null> = of(null);

  private valueSubject = new BehaviorSubject<number | null | undefined>(this.value);
  private currencyFormatter: AppCurrencyFloorPipe;
  private destroyRef = inject(DestroyRef);

  constructor(
    private readonly userInfoProfileService: UserInfoProfileService,
    private readonly currenciesFacade: Currencies,
  ) {
    this.currencyFormatter = new AppCurrencyFloorPipe(this.userInfoProfileService, this.currenciesFacade);
  }

  ngOnInit() {
    this.value$ = combineLatest([this.valueSubject, this.userInfoProfileService.numberLocale$]).pipe(
      takeUntilDestroyed(this.destroyRef),
      map(([value, numberLocale]: [number | null | undefined, Locales]) => {
        if (typeof value !== 'number') {
          return null;
        }

        const valueBN = BigNumber(value);

        return this.formatCurrency(valueBN.toNumber(), numberLocale);
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.value) {
      this.valueSubject.next(changes.value.currentValue);
    }
  }

  private formatCurrency(value: number, numberLocale: string) {
    return this.currencyFormatter.transformNumber(value, numberLocale, this.currency, false);
  }
}
